import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import HeaderLogo from 'components/commercetools-ui/organisms/header/header-logo';
import HeaderNavigationDesktop from 'components/commercetools-ui/organisms/header/header-navigation/header-navigation-desktop';
import HeaderNavigationMobile from 'components/commercetools-ui/organisms/header/header-navigation/header-navigation-mobile';
import { HeaderProps } from 'components/commercetools-ui/organisms/header/types';
import { XMarkIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/solid';
import { usePathname, useSearchParams } from 'next/navigation';
import Link from 'components/commercetools-ui/atoms/link';
import MarketButton from '../market-button/market-button';
import { useAccount } from 'frontastic';
import RegisterPopup from '../authentication/register/register-popup';
import AccountDropdown from './account-dropdown';
import { SITE_URL, FEEDBACK_URL } from 'helpers/constants/envVariables';
import Modal from 'components/commercetools-ui/atoms/modal';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import { desktop } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTranslation from 'providers/i18n/hooks/useTranslation';

const AlgoliaSearch = dynamic(() => import('components/commercetools-ui/atoms/search-algolia'));

const Header: React.FC<HeaderProps> = ({
  navLinks,
  data: propData,
  topRightMenu,
  floatingMenu,
  categories,
  logo,
  logoMobile,
  logoLink,
  logoLinkMobile,
  tiles,
}) => {
  const { loggedIn } = useAccount();
  const [showSearchBar, setShowSearchBar] = useState(false);
  const currentURL = usePathname();
  const searchParams = useSearchParams();
  const isSearchPage = currentURL.includes('/search/');
  const [showSignupPopup, setShowSignupPopup] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const login = searchParams.get('login');
  const error_description = searchParams.get('error_description');
  const [isDesktopSize] = useMediaQuery(desktop);
  const { translate } = useTranslation();
  const errorMessage=error_description ==="This URL can be used only once" ? translate('error.link.already.used'): ''  

   useEffect(() => {
    if (login === 'true') {
      setShowSignupPopup(true);
    }
     // Function to handle screen resize
  const handleResize = () => {
    if (window.innerWidth >= 744 && window.innerWidth < 1024) {
      setIsMenuOpen(true); // Keep menu open by default on medium screens
    }
  };

  window.addEventListener('resize', handleResize);
  handleResize(); // Call it immediately to check on load

  return () => window.removeEventListener('resize', handleResize);
  }, [login]);

  return (
    <>
      <header className="relative h-fit w-full border-b border-neutral-400 bg-white">
        <div
          aria-label="Top"
          className="flex w-full items-center justify-between px-[20.5px] sm:px-24 md:px-34 sm:pt-0 md:pt-16 lg:px-48 xl:px-60 xl:pt-16 2xl:pt-32 3xl:px-80 3xl:pb-16"
        >
          {/* <HeaderNavigationMobile logo={logoMobile} links={navLinks} logoLink={logoLinkMobile} /> */}

          <div className="ml-0 hidden w-full justify-start lg:flex lg:w-fit">
            <HeaderLogo
              logo={logo}
              logoLink={logoLink}
              imageClassName="flex h-58 lg:h-30 w-188 text-16 font-bold lg:w-[13rem] 2xl:w-[18rem]"
            />
          </div>
          <div className="ml-0 flex w-full justify-start lg:hidden">
            <HeaderLogo
              logo={logoMobile}
              logoLink={logoLinkMobile}
              imageClassName="flex h-68 text-16 font-bold w-[12rem]"
            />
          </div>
          {!isSearchPage && (
            <div
              className="search-view ml-auto hover:cursor-pointer lg:hidden"
              onClick={() => setShowSearchBar(!showSearchBar)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path
                  d="M16.0932 16.0934L13.2572 13.2573M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11ZM14.5 10.25C14.5 12.5972 12.5972 14.5 10.25 14.5C7.90279 14.5 6 12.5972 6 10.25C6 7.90279 7.90279 6 10.25 6C12.5972 6 14.5 7.90279 14.5 10.25Z"
                  stroke="#2B2C39"
                />
              </svg>
              {/* <Search categories={categories} /> */}
            </div>
          )}

          {topRightMenu && (
            <div className="hidden lg:block">
              <div className="flex items-center gap-18">
                {topRightMenu.map((link: any) =>
                  link.menu_links.title.toLowerCase() === 'sign up/sign in' ? (
                    <span
                      key={link.menu_links.title}
                      role="button"
                      onClick={() => !loggedIn && setShowSignupPopup(true)}
                      className="group flex items-center text-10 font-semibold text-[#666] hover:cursor-pointer hover:text-[#333] 2xl:text-14"
                    >
                      {loggedIn ? <AccountDropdown /> : link.menu_links.title}
                      {/* <UserCircleIcon className="ml-3  w-16 text-[#666] group-hover:cursor-pointer group-hover:text-[#333]" /> */}
                    </span>
                  ) : (
                    <Link
                      key={link.menu_links.title}
                      link={link.menu_links.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <span className="group flex items-center text-10 font-semibold text-[#666] hover:cursor-pointer hover:text-[#333] 2xl:text-14">
                        {link.menu_links.title}
                      </span>
                    </Link>
                  ),
                )}
                <span className="ml-12">
                  <MarketButton />
                </span>
              </div>
            </div>
          )}
        {/* Floating Menu */}
        <div className="hidden lg:flex fixed right-0 top-[60%] backdrop-blur-lg bg-white/50 shadow-lg rounded-l-lg p-4 flex flex-col space-y-2">
          {floatingMenu.menu_links.map((link: any, index: number) => (
            link.title === 'Share Feedback' ? (
              <span
                key={index}
                className="text-12 text-gray-700 hover:text-gray-900 font-medium cursor-pointer leading-4"
                onClick={() => setIsFeedbackOpen(true)}
              >
                {link.title}
              </span>
            ) : (
              <Link key={index} link={link.href} rel="noopener noreferrer" target="_self" className="leading-4">
                <span className="text-12 text-gray-700 hover:text-gray-900 font-medium leading-4">{link.title}</span>
              </Link>
            )
          ))}
        </div>

        <Modal
          isOpen={isFeedbackOpen}
          onRequestClose={() => setIsFeedbackOpen(false)}
          style={{
            content: { backgroundColor: 'white', padding: '5px', height: isDesktopSize ? '77.5vh' : '65vh', maxWidth: isDesktopSize ? '65vh' : '85%' },
            overlay: { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
          }}
          closeButton
          className="border-transparent focus:border-transparent focus:outline-none focus:ring-0"
        >
          {typeof window !== "undefined" && (
            <embed
              className="h-full w-full"
              src={encodeURI(`${FEEDBACK_URL},"Source":"${encodeURIComponent(`${window.location.origin}`)}"}`)}
            ></embed>
          )}
        </Modal>

        {/* Floating Menu Button for Medium Screens (744px - 1023px) */}
        <div className="fixed right-4 top-1/2 transform -translate-y-1/2 md:block hidden lg:hidden">
          <button
            className="bg-white p-1 rounded-full shadow-lg flex items-center justify-center"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <XMarkIcon className="w-15 h-15 text-gray-700" /> : <PlusIcon className="w-15 h-15 text-gray-700" />}
          </button>
        </div>

        {/* Floating Menu for Medium Screens */}
        {isMenuOpen && (
          <div className="hidden md:flex fixed right-4 top-1/2 transform -translate-y-1/2 backdrop-blur-lg bg-white/50 shadow-lg rounded-lg p-4 flex flex-col space-y-2 overflow-auto">
            <button
              className="absolute top-2 right-2 p-1"
              onClick={() => setIsMenuOpen(false)}
            >
              <XMarkIcon className="w-15 h-15 text-gray-700" />
            </button>
            {floatingMenu.menu_links.map((link: any, index: number) => (
              link.title === 'Share Feedback' ? (
                <span
                  key={index}
                  className="text-12 text-gray-700 hover:text-gray-900 font-medium cursor-pointer leading-4"
                  onClick={() => setIsFeedbackOpen(true)}
                >
                  {link.title}
                </span>
              ) : (
                <Link key={index} link={link.href} rel="noopener noreferrer" target="_self" className="leading-4">
                  <span className="text-12 text-gray-700 hover:text-gray-900 font-medium leading-4">{link.title}</span>
                </Link>
              )
            ))}
          </div>
        )}

          <HeaderNavigationMobile
            logo={logoMobile}
            links={navLinks}
            topRightMenu={topRightMenu}
            logoLink={logoLinkMobile}
            showSearchBar={showSearchBar}
            setShowSearchBar={setShowSearchBar}
            setShowSignupPopup={setShowSignupPopup}
          />
        </div>

        <div className="flex justify-between">
          <HeaderNavigationDesktop
            links={navLinks}
            tiles={tiles ?? []}
            showSearchBar={showSearchBar}
            categories={categories}
            setShowSearchBar={setShowSearchBar}
          />
          <div className="hidden lg:block">
            <div className="flex items-center px-48 xl:pr-58 2xl:pr-60 3xl:pr-80">
              <div
                className="-mt-2 ml-auto py-10 hover:cursor-pointer 3xl:-mt-0"
                onClick={() => setShowSearchBar(!showSearchBar)}
              >
                {!isSearchPage && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 26 26" fill="none">
                    <path
                      d="M22.75 22.75L16.25 16.25M18.4167 10.8333C18.4167 15.0215 15.0215 18.4167 10.8333 18.4167C6.64517 18.4167 3.25 15.0215 3.25 10.8333C3.25 6.64517 6.64517 3.25 10.8333 3.25C15.0215 3.25 18.4167 6.64517 18.4167 10.8333Z"
                      stroke="#666666"
                      strokeWidth="2"
                      strokeLinecap="square"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {/* To be used later in search funciotnality */}
                {/* <Search categories={categories} /> */}
              </div>
            </div>
          </div>
        </div>
        <div className={`${showSearchBar ? 'block' : 'hidden'} relative z-[1000] bg-white md:px-24 md:py-12 lg:hidden`}>
          <AlgoliaSearch categories={categories} showSearchBar={showSearchBar} setShowSearchBar={setShowSearchBar} />
          <XMarkIcon
            className="absolute right-[1.6rem] top-1/2 z-[999] !h-24 !w-24 -translate-y-1/2 stroke-2 text-primary-black hover:cursor-pointer lg:right-[4.5rem]"
            onClick={() => setShowSearchBar(false)}
          />
          <MagnifyingGlassIcon
            className="stroke-10 absolute left-[2rem] top-1/2 z-[999] h-24 w-24 -translate-y-1/2 text-[#666] lg:hidden"
            onClick={() => setShowSearchBar(false)}
          />
        </div>
      </header>
      <RegisterPopup
        showPopup={showSignupPopup}
        setShowPopup={setShowSignupPopup}
        showLogin={login === 'true' ? true : false}
        error_description={errorMessage}
      />
    </>
  );
};
export default Header;
